import {useCallback} from 'react';
import {t} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {ViewerSetting} from '../../../../../../design-system/organisms/ViewerSetting';
import {ViewerSettingFontType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingFontItemType.interface';
import {ViewerSettingReadType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingReadTypeProps.interface';
import {ViewerSettingThemeType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingThemeItemType.interface';
import {
  CONTENT_MARGIN_MAX,
  CONTENT_MARGIN_MIN,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  LINE_GAP_MAX,
  LINE_GAP_MIN,
  ViewerType,
} from '../constant/viewerConstant';
import {
  changeFontFamily,
  changeReadType,
  changeTheme,
  decreaseContentMargin,
  decreaseFontSize,
  decreaseLineGap,
  increaseContentMargin,
  increaseFontSize,
  increaseLineGap,
  resetContentOptions,
  toggleTwoPageLayout,
} from '../state/textViewerSlice';
import {setCurrentViewerGuide} from '../state/viewerInfoSlice';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {convertViewerReadTypeToGuideType} from '../util/convertViewerReadTypeToGuideType';

export function TextViewerPreviewSettingContainer({...props}) {
  const {isMobile} = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();

  const {
    textViewerReadType,
    textViewerTheme,
    textViewerFontFamily,
    textViewerFontSize,
    textViewerLineGap,
    textViewerContentMargin,
    textViewerIsTwoPageLayout,
  } = useTypedSelector(state => ({
    textViewerReadType: state.textViewer.settings.readType,
    textViewerTheme: state.textViewer.settings.theme,
    textViewerFontFamily: state.textViewer.settings.fontFamily,
    textViewerFontSize: state.textViewer.settings.fontSize,
    textViewerLineGap: state.textViewer.settings.lineGap,
    textViewerContentMargin: state.textViewer.settings.contentMargin,
    textViewerIsTwoPageLayout: state.textViewer.settings.isTwoPageLayout,
  }));

  const onClickReadType = useCallback(
    (clickedReadType: ViewerSettingReadType) => {
      const clickedTextViewerReadType =
        clickedReadType === 'Type1'
          ? TextViewerReadType.Page
          : TextViewerReadType.Scroll;
      dispatch(changeReadType(clickedTextViewerReadType));
      dispatch(
        setCurrentViewerGuide(
          convertViewerReadTypeToGuideType({
            viewerType: ViewerType.TextViewer,
            viewerReadType: clickedTextViewerReadType,
          })
        )
      );
    },
    [dispatch]
  );

  const onClickTheme = useCallback(
    (clickedTheme: ViewerSettingThemeType) => {
      dispatch(changeTheme(clickedTheme));
    },
    [dispatch]
  );

  const onClickFontFamily = useCallback(
    (clickedFontType: ViewerSettingFontType) => {
      dispatch(changeFontFamily(clickedFontType));
    },
    [dispatch]
  );

  const onClickFontSizeMinus = useCallback(() => {
    dispatch(decreaseFontSize());
  }, [dispatch]);

  const onClickFontSizePlus = useCallback(() => {
    dispatch(increaseFontSize());
  }, [dispatch]);

  const onClickLineGapMinus = useCallback(() => {
    dispatch(decreaseLineGap());
  }, [dispatch]);

  const onClickLineGapPlus = useCallback(() => {
    dispatch(increaseLineGap());
  }, [dispatch]);

  const onClickContentMarginMinus = useCallback(() => {
    dispatch(decreaseContentMargin());
  }, [dispatch]);

  const onClickContentMarginPlus = useCallback(() => {
    dispatch(increaseContentMargin());
  }, [dispatch]);

  //초기화 버튼 클릭. 로컬스토리지, 리덕스를 기본값으로 초기화
  const onClickResetContentOptions = useCallback(() => {
    dispatch(resetContentOptions());
  }, [dispatch]);

  const onClickTwoLayoutToggle = useCallback(() => {
    dispatch(toggleTwoPageLayout());
  }, [dispatch]);

  return (
    <ViewerSetting {...props}>
      <ViewerSetting.ReadType
        isMobile={isMobile}
        readType1={t('viewer_setting_mode_page_button')}
        readType2={t('viewer_setting_mode_scroll_button')}
        currentReadType={
          textViewerReadType === TextViewerReadType.Page ? 'Type1' : 'Type2'
        }
        onClickReadType={onClickReadType}
        className="px-24pxr"
      />
      <ViewerSetting.Divider isMobile={isMobile} />
      {/* 콘텐츠 보기 설정 영역 */}
      <div className="px-24pxr">
        <ViewerSetting.Theme
          isMobile={isMobile}
          currentTheme={textViewerTheme}
          onClickTheme={onClickTheme}
        />
        <ViewerSetting.FontFamily
          isMobile={isMobile}
          currentFontType={textViewerFontFamily}
          onClickFontFamily={onClickFontFamily}
        />
        <ViewerSetting.FontSize
          isMobile={isMobile}
          currentFontSize={textViewerFontSize}
          onClickFontSizeMinus={onClickFontSizeMinus}
          onClickFontSizePlus={onClickFontSizePlus}
          isFontSizeMinusDisabled={textViewerFontSize <= FONT_SIZE_MIN}
          isFontSizePlusDisabled={textViewerFontSize >= FONT_SIZE_MAX}
        />
        <ViewerSetting.LineGap
          isMobile={isMobile}
          currentLineGap={textViewerLineGap}
          onClickLineGapMinus={onClickLineGapMinus}
          onClickLineGapPlus={onClickLineGapPlus}
          isLineGapMinusDisabled={textViewerLineGap <= LINE_GAP_MIN}
          isLineGapPlusDisabled={textViewerLineGap >= LINE_GAP_MAX}
        />
        <ViewerSetting.ContentMargin
          isMobile={isMobile}
          currentContentMargin={textViewerContentMargin}
          onClickContentMarginMinus={onClickContentMarginMinus}
          onClickContentMarginPlus={onClickContentMarginPlus}
          isContentMarginMinusDisabled={
            textViewerContentMargin <= CONTENT_MARGIN_MIN
          }
          isContentMarginPlusDisabled={
            textViewerContentMargin >= CONTENT_MARGIN_MAX
          }
        />
        <ViewerSetting.ResetContentOptions
          isMobile={isMobile}
          onClickResetContentOptions={onClickResetContentOptions}
        />
      </div>

      {!isMobile && (
        <div>
          <ViewerSetting.Divider isMobile={isMobile} />
          <ViewerSetting.TwoPageLayout
            isMobile={isMobile}
            isDisabled={textViewerReadType === TextViewerReadType.Scroll}
            isTwoPageLayout={textViewerIsTwoPageLayout}
            onClickTwoLayoutToggle={onClickTwoLayoutToggle}
            className="px-24pxr"
          />
        </div>
      )}
    </ViewerSetting>
  );
}
